import React, { useCallback, useEffect, useState } from 'react';
import { Fingerprint, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  enrollPasskey,
  hasLocalPasskeyFlag,
  isPasskeySupportedOnThisDevice,
} from '../utils/passkeys';
import { mirrorLocalKey } from '../utils/authStorage';

/**
 * One-time post-login prompt that offers biometric unlock enrollment.
 *
 * Fires when ALL of the following are true:
 *   1. `handleLogin` just ran (sessionStorage `allure_passkey_prompt_pending`).
 *   2. The device actually supports a platform authenticator.
 *   3. The user hasn't already enrolled (`hasLocalPasskeyFlag()` is false).
 *   4. The user hasn't dismissed the prompt in the last `COOLDOWN_DAYS` days.
 *
 * "Maybe later" sets a 3-day cooldown so we gently remind users about
 * biometric protection — accounts have bank details and payment methods
 * saved, so we err on the side of nudging every few days rather than
 * once a month.
 * "Enable" triggers the standard `enrollPasskey()` flow, then closes.
 */
const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = '#703FF6';

const PENDING_KEY = 'allure_passkey_prompt_pending';
const DISMISSED_KEY = 'allure_passkey_prompt_dismissed_at';
const COOLDOWN_DAYS = 3;
const LANDING_DELAY_MS = 1500;

const _guessDeviceLabel = () => {
  const ua = navigator.userAgent || '';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android phone';
  if (/Mac OS/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  return 'This device';
};

export const PasskeyEnrollmentPrompt = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const dismissForCooldown = useCallback(() => {
    try {
      const now = String(Date.now());
      window.localStorage.setItem(DISMISSED_KEY, now);
      // Also mirror to Capacitor Preferences so the cooldown survives
      // an APK update — otherwise every update re-prompts on next open.
      mirrorLocalKey(DISMISSED_KEY, now);
    } catch {
      /* noop */
    }
  }, []);

  // Decide whether to show the prompt whenever `user` becomes available.
  useEffect(() => {
    let cancelled = false;
    const decide = async () => {
      if (!user) return;
      let pending = false;
      try {
        pending = window.sessionStorage.getItem(PENDING_KEY) === '1';
      } catch {
        /* noop */
      }
      if (!pending) return;
      // Always clear the flag early so we never re-trigger on route changes.
      try { window.sessionStorage.removeItem(PENDING_KEY); } catch { /* noop */ }

      if (hasLocalPasskeyFlag()) return;

      try {
        const dismissedAt = window.localStorage.getItem(DISMISSED_KEY);
        if (dismissedAt) {
          const ageDays = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
          if (Number.isFinite(ageDays) && ageDays < COOLDOWN_DAYS) return;
        }
      } catch {
        /* noop */
      }

      const supported = await isPasskeySupportedOnThisDevice();
      if (cancelled || !supported) return;

      // Tiny delay so the user has visually arrived on their post-login screen
      // before a modal pops on top of the route transition.
      window.setTimeout(() => {
        if (!cancelled) setOpen(true);
      }, LANDING_DELAY_MS);
    };
    decide();
    return () => { cancelled = true; };
  }, [user]);

  const handleEnable = async () => {
    if (enrolling) return;
    const token = (() => {
      try { return window.localStorage.getItem('token'); } catch { return null; }
    })();
    if (!token) {
      toast.error('Please sign in again to enable biometric unlock.');
      setOpen(false);
      return;
    }
    setEnrolling(true);
    try {
      await enrollPasskey({ token, deviceLabel: _guessDeviceLabel() });
      toast.success('Biometric unlock enabled — sign in with your fingerprint next time.');
      setOpen(false);
    } catch (err) {
      const name = String(err?.name || err?.message || '');
      if (/NotAllowedError|cancel|aborted/i.test(name)) {
        toast.message('Setup cancelled. You can enable it later in Settings.');
      } else {
        toast.error(err?.response?.data?.detail || 'Could not set up biometric unlock. Try again from Settings.');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleLater = () => {
    dismissForCooldown();
    setOpen(false);
  };

  if (!open) return null;

  // Custom modal (not the shadcn Dialog) so we control the soft, branded look.
  // Pointer-events on the overlay ensure taps outside the sheet do nothing —
  // user must explicitly choose "Enable" or "Maybe later".
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="passkey-prompt-title"
      data-testid="passkey-enrollment-prompt"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10400,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(20, 12, 44, 0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        padding: '20px',
        animation: 'allure-passkey-fade 200ms ease-out',
      }}
    >
      <style>{`
        @keyframes allure-passkey-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes allure-passkey-slide { from { transform: translateY(40px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--surface)',
          borderRadius: 24,
          padding: '28px 22px 22px',
          boxShadow: 'var(--premium-shadow-main)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          marginBottom: 'max(env(safe-area-inset-bottom), 24px)',
          animation: 'allure-passkey-slide 260ms cubic-bezier(0.22, 1, 0.36, 1)',
          position: 'relative',
        }}
      >
        <button
          onClick={handleLater}
          aria-label="Dismiss biometric unlock prompt"
          data-testid="passkey-enrollment-prompt-close"
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: 'none',
            background: 'var(--accent-surface)',
            color: 'var(--text-2)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={16} strokeWidth={2.2} />
        </button>

        <div
          style={{
            alignSelf: 'center',
            width: 64,
            height: 64,
            borderRadius: 20,
            background: 'linear-gradient(135deg, rgba(112, 63, 246, 0.14), rgba(74, 32, 201, 0.18))',
            color: BRAND,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 4,
          }}
        >
          <Fingerprint size={32} strokeWidth={2} />
        </div>

        <div style={{ textAlign: 'center' }}>
          <h2
            id="passkey-prompt-title"
            style={{
              margin: 0,
              fontFamily: FONT,
              fontSize: 22,
              fontWeight: 700,
              color: 'var(--text)',
              lineHeight: 1.25,
            }}
          >
            Skip the password next time
          </h2>
          <p
            style={{
              margin: '10px 0 0',
              fontFamily: SUB_FONT,
              fontSize: 14,
              color: 'var(--text-2)',
              lineHeight: 1.5,
            }}
          >
            Unlock Allure with your fingerprint, Face ID or device PIN — fast, private, and never leaves your phone.
          </p>
        </div>

        <button
          onClick={handleEnable}
          disabled={enrolling}
          data-testid="passkey-enrollment-prompt-enable"
          style={{
            width: '100%',
            padding: '15px 16px',
            borderRadius: 16,
            border: 'none',
            background: enrolling
              ? 'rgba(112, 63, 246, 0.5)'
              : 'linear-gradient(135deg, #703FF6 0%, #4A20C9 100%)',
            color: '#FFFFFF',
            fontFamily: SUB_FONT,
            fontSize: 15,
            fontWeight: 700,
            cursor: enrolling ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            boxShadow: '0 10px 24px rgba(112, 63, 246, 0.3)',
          }}
        >
          <ShieldCheck size={18} strokeWidth={2.2} />
          {enrolling ? 'Setting up…' : 'Enable biometric unlock'}
        </button>

        <button
          onClick={handleLater}
          disabled={enrolling}
          data-testid="passkey-enrollment-prompt-later"
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 16,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-2)',
            fontFamily: SUB_FONT,
            fontSize: 14,
            fontWeight: 600,
            cursor: enrolling ? 'wait' : 'pointer',
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default PasskeyEnrollmentPrompt;
